import { API_BASE_URL } from "../../constant/constant";
import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { TextField, CircularProgress, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  ModalOverlay,
  UploadContent,
  UploadTxt,
  ContentDiv,
  TaskTitle,
  TaskDescription,
  ScheduleDiv,
  CustomDiv,
  TaskTxt,
  TabsDiv,
  Tab,
  ModalBtn,
} from "./Style";

import CloseX from "../../assets/x-close.svg";
import Schedule from "../../assets/schedule.svg";
import Sort from "../../assets/sort.svg";
import InfoIcon from "../../assets/info.svg";
import { CustomDateTimePicker, CustomSelect } from "../../utils/utils";
import {
  recurrenceOptions,
  weekDayOptions,
  TimeInfo,
} from "../../Components/Constants/Constants";
import { useSnackbar } from "../../context/SnackBarContext";

const TaskModal = ({ closeAddTaskModal, fetchEvents }) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startdatetime, setStartDatetime] = useState(dayjs().add(1, "hour"));
  const [enddatetime, setEndDatetime] = useState(dayjs().add(2, "hour"));
  const [recurrenceType, setRecurrenceType] = useState("One Time");
  const [selectedWeekDays, setSelectedWeekDays] = useState(["MO"]);
  const [selectedDates, setSelectedDates] = useState([1]);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState();
  const [selectedOccurrence, setSelectedOccurrence] = useState();
  const [error, setError] = useState("");
  const [selectedTab, setSelectedTab] = useState("Task");
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  const showSnackbar = useSnackbar();

  const monthDates = Array.from({ length: 31 }, (_, i) => i + 1);
  const dateOptions = monthDates.map((date) => ({
    value: date,
    label: date.toString(),
  }));

  useEffect(() => {
    setEndDatetime(dayjs(startdatetime).add(1, "hour"));
  }, [startdatetime]);

  useEffect(() => {
    if (recurrenceType === "daily") {
      setSelectedWeekDays(["MO"]);
      setSelectedDates([1]);
      setSelectedDayOfWeek("");
      setSelectedOccurrence("");
    }
  }, [recurrenceType]);

  useEffect(() => {
    if (selectedTab === "Workflow") {
      const fetchWorkflows = async () => {
        try {
          const userId = localStorage.getItem("id");
          const token = localStorage.getItem("token");
          const response = await fetch(
            `${API_BASE_URL}/api/workflow/getworkflows?user_id=${userId}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "x-auth-token": token || "",
              },
            }
          );
          const data = await response.json();

          if (data.responseCode === 200) {
            setWorkflows(data.workflows || []);
          }
          if (data.responseCode === 429) {
            showSnackbar(data.message, "error");
          }
        } catch (error) {
          console.error("Failed to fetch workflows:", error);
        }
      };
      fetchWorkflows();
    }
  }, [selectedTab]);

  const workflowOptions = workflows.map((workflow) => ({
    value: workflow._id,
    label: workflow.name,
  }));

  const handleSubmit = async () => {
    if (selectedTab === "Task" && (!title.trim() || !description.trim())) {
      setError(t("taskModal.errors.taskRequired"));
      return;
    }

    if (selectedTab === "Workflow" && !selectedWorkflow) {
      setError(t("taskModal.errors.workflowRequired"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const userId = localStorage.getItem("id");

      const payload = {
        userId,
        start: startdatetime.toISOString(),
        end: enddatetime.toISOString(),
        isRecurring: recurrenceType !== "One Time",
        timestamp: new Date().toISOString(),
      };

      if (selectedTab === "Task") {
        payload.taskTitle = title;
        payload.taskDescription = description;
      } else {
        const selectedWorkflowData = workflows.find(
          (w) => w._id === selectedWorkflow,
        );
        payload.workflow_id = selectedWorkflow;
        payload.workflow_title = selectedWorkflowData.name;
        payload.workflow_description = selectedWorkflowData.description;
        payload.user_email = localStorage.getItem("email");
        payload.user_name = localStorage.getItem("name");
      }

      if (recurrenceType !== "One Time") {
        payload.recurrenceType = recurrenceType;

        if (recurrenceType === "weekly") {
          payload.byweekday = selectedWeekDays;
        }
      }

      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/task`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token || "",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.responseCode === 429) {
        showSnackbar(data.message, "warning");
        return;
      }

      if (!response.ok) {
        throw new Error();
      }

      await fetchEvents();
      closeAddTaskModal();
    } catch {
      setError(t("taskModal.errors.createFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalOverlay>
      <UploadContent style={{ height: "90%", gap: "1rem" }}>
        <div style={{ display: "flex", marginLeft: "auto" }}>
          <img src={CloseX} alt="close" onClick={closeAddTaskModal} />
        </div>

        <TabsDiv>
          <Tab
            onClick={() => setSelectedTab("Task")}
            style={{
              backgroundColor: selectedTab === "Task" ? "#51307F" : "#F1F1F5",
              color: selectedTab === "Task" ? "white" : "inherit",
            }}
          >
            {t("taskModal.tabs.task")}
          </Tab>
          <Tab
            onClick={() => setSelectedTab("Workflow")}
            style={{
              backgroundColor:
                selectedTab === "Workflow" ? "#51307F" : "#F1F1F5",
              color: selectedTab === "Workflow" ? "white" : "inherit",
            }}
          >
            {t("taskModal.tabs.workflow")}
          </Tab>
        </TabsDiv>

        <ContentDiv>
          {selectedTab === "Task" ? (
            <>
              <TextField
                placeholder={t("taskModal.placeholders.title")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
              />
              <TaskDescription
                as="textarea"
                placeholder={t("taskModal.placeholders.description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </>
          ) : (
            <CustomSelect
              value={selectedWorkflow}
              onChange={(e) => setSelectedWorkflow(e.target.value)}
              options={workflowOptions}
              placeholder={t("taskModal.placeholders.workflow")}
            />
          )}

          <ScheduleDiv>
            <img src={Schedule} alt="schedule" />
            <CustomDateTimePicker
              label={t("taskModal.startDate")}
              value={startdatetime}
              onChange={setStartDatetime}
              disablePast="disablePast"
            />
            <Tooltip title={TimeInfo}>
              <img src={InfoIcon} alt="info" />
            </Tooltip>
          </ScheduleDiv>

          <CustomDiv>
            <TaskTitle>{t("taskModal.recurrence")}</TaskTitle>
            <ScheduleDiv>
              <img src={Sort} alt="" />
              <CustomSelect
                value={recurrenceType}
                onChange={(e) => setRecurrenceType(e.target.value)}
                options={recurrenceOptions}
              />
            </ScheduleDiv>

            {recurrenceType === "weekly" && (
              <ScheduleDiv>
                <TaskTxt>{t("taskModal.repeatOn")}</TaskTxt>
                <CustomSelect
                  value={selectedWeekDays[0]}
                  onChange={(e) => setSelectedWeekDays([e.target.value])}
                  options={weekDayOptions}
                />
              </ScheduleDiv>
            )}
          </CustomDiv>
        </ContentDiv>

        {error && (
          <div style={{ color: "red", textAlign: "center" }}>{error}</div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "auto",
          }}
        >
          <UploadTxt onClick={closeAddTaskModal}>
            {t("taskModal.skip")}
          </UploadTxt>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <ModalBtn onClick={handleSubmit}>{t("taskModal.done")}</ModalBtn>
          )}
        </div>
      </UploadContent>
    </ModalOverlay>
  );
};

export default TaskModal;
